import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Search, MapPin, Globe, Loader2 } from "lucide-react";
import {
  Command,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { trpc } from "@/lib/trpc";
import { useDebounce } from "@/hooks/useDebounce";
import { haversineDistance } from "@/lib/geo";
import { useSearchContext } from "@/contexts/SearchContext";
import type { MarkerDatum } from "@/hooks/useMapData";

const PROXIMITY_THRESHOLD_METERS = 200;

/** Check if the Google Maps Places library is loaded globally. */
function getPlacesLib() {
  return typeof google !== "undefined" ? google.maps?.places : null;
}

export function HeaderSearchBar() {
  const navigate = useNavigate();
  const { isOnMapPage, markerDataRef, mapBoundsRef, onSearchSelectRef } =
    useSearchContext();

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Google Places services — initialised from the global google.maps.places
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const sessionToken =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Initialise Google services when we enter the map page.
  // The Places library may still be loading, so poll until it's available.
  const placesElRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isOnMapPage) {
      autocompleteService.current = null;
      placesService.current = null;
      sessionToken.current = null;
      setPredictions([]);
      if (placesElRef.current) {
        placesElRef.current.remove();
        placesElRef.current = null;
      }
      return;
    }

    function tryInit() {
      const places = getPlacesLib();
      if (!places) return false;
      autocompleteService.current = new places.AutocompleteService();
      const el = document.createElement("div");
      el.style.display = "none";
      document.body.appendChild(el);
      placesElRef.current = el;
      placesService.current = new places.PlacesService(el);
      return true;
    }

    if (tryInit()) return () => { placesElRef.current?.remove(); placesElRef.current = null; };

    // Poll every 200ms until the Places library loads (max 5s)
    let attempts = 0;
    const timer = setInterval(() => {
      attempts++;
      if (tryInit() || attempts > 25) clearInterval(timer);
    }, 200);
    return () => {
      clearInterval(timer);
      placesElRef.current?.remove();
      placesElRef.current = null;
    };
  }, [isOnMapPage]);

  // tRPC search — for non-map pages
  const { data: trpcResults, isFetching: trpcLoading } =
    trpc.locations.search.useQuery(
      { query: debouncedQuery, limit: 10 },
      { enabled: !isOnMapPage && debouncedQuery.length >= 2 },
    );

  // Local search: filter markerData by query + viewport bounds (map page only).
  // Reads from refs — recomputes only when debouncedQuery changes.
  const localResults = useMemo(() => {
    if (!isOnMapPage || debouncedQuery.length < 2) return [];
    const data = markerDataRef.current;
    const bounds = mapBoundsRef.current;
    const q = debouncedQuery.toLowerCase();
    return data
      .filter((loc) => {
        if (bounds && !bounds.contains({ lat: loc.lat, lng: loc.lng }))
          return false;
        const nameMatch = loc.name.toLowerCase().includes(q);
        const addrMatch = loc.address?.toLowerCase().includes(q) ?? false;
        return nameMatch || addrMatch;
      })
      .slice(0, 5);
  }, [debouncedQuery, isOnMapPage, markerDataRef, mapBoundsRef]);

  // Fetch Google predictions (map page only)
  useEffect(() => {
    if (
      !isOnMapPage ||
      debouncedQuery.length < 2 ||
      !autocompleteService.current
    ) {
      setPredictions([]);
      return;
    }

    const places = getPlacesLib();
    if (!sessionToken.current && places) {
      sessionToken.current = new places.AutocompleteSessionToken();
    }

    setGoogleLoading(true);
    const bounds = mapBoundsRef.current;
    const request: google.maps.places.AutocompletionRequest = {
      input: debouncedQuery,
      sessionToken: sessionToken.current!,
      ...(bounds ? { locationBias: bounds } : {}),
    };

    autocompleteService.current.getPlacePredictions(
      request,
      (results, status) => {
        setGoogleLoading(false);
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results.slice(0, 5));
        } else {
          setPredictions([]);
        }
      },
    );
  }, [debouncedQuery, isOnMapPage, mapBoundsRef]);

  // Find nearest local marker within threshold
  const findNearbyMarker = useCallback(
    (lat: number, lng: number): MarkerDatum | null => {
      const data = markerDataRef.current;
      let closest: MarkerDatum | null = null;
      let closestDist = PROXIMITY_THRESHOLD_METERS;
      for (const m of data) {
        const dist = haversineDistance(lat, lng, m.lat, m.lng);
        if (dist < closestDist) {
          closestDist = dist;
          closest = m;
        }
      }
      return closest;
    },
    [markerDataRef],
  );

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
  }, []);

  // Select a local / tRPC result
  const handleLocalSelect = useCallback(
    (id: string) => {
      const handler = onSearchSelectRef.current;
      if (isOnMapPage && handler) {
        handler(id);
      } else {
        navigate({ to: "/dashboard", search: { location: id } });
      }
      closeDropdown();
    },
    [isOnMapPage, onSearchSelectRef, navigate, closeDropdown],
  );

  // Select a Google prediction (map page only)
  const handleGoogleSelect = useCallback(
    (placeId: string) => {
      const handler = onSearchSelectRef.current;
      if (!placesService.current || !handler) return;

      placesService.current.getDetails(
        { placeId, fields: ["geometry"], sessionToken: sessionToken.current! },
        (place, status) => {
          sessionToken.current = null;
          if (
            status !== google.maps.places.PlacesServiceStatus.OK ||
            !place?.geometry?.location
          )
            return;

          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const nearby = findNearbyMarker(lat, lng);
          if (nearby) {
            handler(nearby.id);
          } else {
            handler(`__pan:${lat}:${lng}`);
          }
        },
      );
      closeDropdown();
    },
    [findNearbyMarker, onSearchSelectRef, closeDropdown],
  );

  // Close on Escape
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") closeDropdown();
    },
    [closeDropdown],
  );

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasQuery = debouncedQuery.length >= 2;

  // Results for display
  const scannedResults = isOnMapPage ? localResults : (trpcResults ?? []);
  const hasScannedResults = scannedResults.length > 0;
  const hasGoogleResults = isOnMapPage && predictions.length > 0;
  const hasAnyResults = hasScannedResults || hasGoogleResults;
  const isLoading = isOnMapPage ? googleLoading : trpcLoading;

  return (
    <div
      ref={containerRef}
      className="relative hidden md:block"
      onKeyDown={handleKeyDown}
    >
      <Command
        shouldFilter={false}
        className="rounded-lg border-none bg-transparent"
      >
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search locations..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (e.target.value.length >= 2) setOpen(true);
              if (e.target.value.length === 0) setOpen(false);
            }}
            onFocus={() => {
              if (query.length >= 2) setOpen(true);
            }}
            className="h-9 w-[200px] rounded-md border border-border bg-muted/50 pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:border-accent focus:ring-1 focus:ring-accent/50 focus:outline-none lg:w-[280px]"
          />
        </div>

        {open && hasQuery && (
          <CommandList className="absolute top-full left-0 mt-1 w-[320px] rounded-lg border bg-popover shadow-lg z-50">
            {isLoading && !hasAnyResults && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              </div>
            )}

            {!isLoading && !hasAnyResults && (
              <CommandEmpty>No results found.</CommandEmpty>
            )}

            {hasScannedResults && (
              <CommandGroup
                heading={
                  isOnMapPage ? "Locations in View" : "Scanned Locations"
                }
              >
                {scannedResults.map((loc: any) => (
                  <CommandItem
                    key={loc.id}
                    value={`local-${loc.id}`}
                    onSelect={() => handleLocalSelect(loc.id)}
                  >
                    <MapPin className="size-4 text-green-600 shrink-0" />
                    <div className="flex flex-col min-w-0">
                      <span className="truncate text-sm">{loc.name}</span>
                      {loc.address && (
                        <span className="truncate text-xs text-muted-foreground">
                          {loc.address}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {hasGoogleResults && (
              <CommandGroup heading="Google Places">
                {predictions.map((pred) => (
                  <CommandItem
                    key={pred.place_id}
                    value={`google-${pred.place_id}`}
                    onSelect={() => handleGoogleSelect(pred.place_id)}
                  >
                    <Globe className="size-4 text-blue-500 shrink-0" />
                    <div className="flex flex-col min-w-0">
                      <span className="truncate text-sm">
                        {pred.structured_formatting.main_text}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {pred.structured_formatting.secondary_text}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {isLoading && hasAnyResults && (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="size-3 animate-spin text-muted-foreground" />
              </div>
            )}
          </CommandList>
        )}
      </Command>
    </div>
  );
}
